import { requestGet as wsRequestGet, requestPost as wsRequestPost, requestDelete as wsRequestDelete } from './_wsApiFetchData';

// Contract: docs/clubhouse/API-CONTRACT.md (Api.WannaSport), section "Admin endpoints".
const base = '/admin/ClubhouseAdmin';

export async function getClubhouses(query, language, timeZone, cancelToken) {
    return await wsRequestGet(`${base}/clubhouses`, language, timeZone, query, cancelToken);
}

export async function getClubhouse(slug, language, timeZone, cancelToken) {
    return await wsRequestGet(`${base}/clubhouses/${encodeURIComponent(slug)}`, language, timeZone, null, cancelToken);
}

export async function getMemberships(query, language, timeZone, cancelToken) {
    return await wsRequestGet(`${base}/memberships`, language, timeZone, query, cancelToken);
}

export async function getWannas(query, language, timeZone, cancelToken) {
    return await wsRequestGet(`${base}/wannas`, language, timeZone, query, cancelToken);
}

export async function getWanna(code, language, timeZone, cancelToken) {
    return await wsRequestGet(`${base}/wannas/${encodeURIComponent(code)}`, language, timeZone, null, cancelToken);
}

// Soft delete. The body is optional: no reason means no body.
export async function deleteWanna(code, reason, language, timeZone, cancelToken) {
    return await wsRequestDelete(`${base}/wannas/${encodeURIComponent(code)}`, language, timeZone, reason ? { reason } : null, null, cancelToken);
}

// Cancels the game and refunds every settled charge. The reason is required by the API.
export async function cancelWanna(code, reason, language, timeZone, cancelToken) {
    return await wsRequestPost(`${base}/wannas/${encodeURIComponent(code)}/cancel`, language, timeZone, { reason }, null, cancelToken);
}

export async function getPayments(query, language, timeZone, cancelToken) {
    return await wsRequestGet(`${base}/payments`, language, timeZone, query, cancelToken);
}

// Full refund when amountMinor is null; partial refund otherwise. withdraw also takes the player out of the game
// (seat freed); without it the refund only moves money.
export async function refundPayment(orderHandle, amountMinor, withdraw, language, timeZone, cancelToken) {
    const query = { ...(amountMinor ? { amountMinor } : {}), ...(withdraw ? { withdraw: true } : {}) };
    return await wsRequestPost(`${base}/payments/${encodeURIComponent(orderHandle)}/refund`, language, timeZone, null, Object.keys(query).length ? query : null, cancelToken);
}

// Creates a clubhouse. The creator is not added as a member.
export async function createClubhouse(body, language, timeZone, cancelToken) {
    return await wsRequestPost(`${base}/clubhouses`, language, timeZone, body, null, cancelToken);
}

// Edits name, texts and place of a clubhouse. Every field is written; slug and kind never change.
export async function updateClubhouse(slug, body, language, timeZone, cancelToken) {
    return await wsRequestPost(`${base}/clubhouses/${encodeURIComponent(slug)}`, language, timeZone, body, null, cancelToken);
}

// Areas a clubhouse can sit in, as [{ value: slug, label }]. Searched by name and by slug, so both "København"
// and "koebenhavn" find it. Regions (super areas: Jylland, Sjælland, ...) are left out: they have no map
// position and the API refuses them as a clubhouse place.
export async function lookupAreaSlugs(key, language, timeZone, cancelToken) {
    const [byName, bySlug] = await Promise.all([
        wsRequestGet('/admin/areas/search', language, timeZone, { Name: key }, cancelToken),
        wsRequestGet('/admin/areas/search', language, timeZone, { Slug: key }, cancelToken),
    ]);
    const options = new Map();
    [...(byName?.areas || []), ...(bySlug?.areas || [])]
        .filter((area) => !(area.isSuperArea ?? area.IsSuperArea))
        .forEach((area) => {
            const slug = area.slug || area.Slug;
            options.set(slug, { value: slug, label: `${area.name || area.Name} (${(area.countryCode || area.CountryCode || '').toUpperCase()})` });
        });
    return [...options.values()];
}

// Facilities a facility clubhouse can point to, as [{ value: slug, label }]. Uses the public clubhouse list, which
// holds exactly the facilities the clubhouse shows (a future activity and a sport we present).
export async function lookupFacilitySlugs(key, language, timeZone, cancelToken) {
    const result = await wsRequestGet('/public/clubhouse/facilities', language, timeZone, { q: key, take: 10 }, cancelToken);
    return (result?.facilities || []).map((facility) => ({ value: facility.slug, label: `${facility.name} (${facility.areaSlug || '-'})` }));
}

// Adds an existing user to a clubhouse. The role is "member" (default) or "admin".
export async function addClubhouseMember(slug, userId, role, language, timeZone, cancelToken) {
    return await wsRequestPost(`${base}/clubhouses/${encodeURIComponent(slug)}/members`, language, timeZone, { userId, role }, null, cancelToken);
}

// Removes a member from a clubhouse. The member's wannas stay.
export async function removeClubhouseMember(slug, userId, language, timeZone, cancelToken) {
    return await wsRequestDelete(`${base}/clubhouses/${encodeURIComponent(slug)}/members/${encodeURIComponent(userId)}`, language, timeZone, null, null, cancelToken);
}

// Creates an invite link for a clubhouse. The token is shown once; expiry is 1-30 days (the API default is 14).
export async function createClubhouseInvite(slug, expiresInDays, language, timeZone, cancelToken) {
    return await wsRequestPost(`${base}/clubhouses/${encodeURIComponent(slug)}/invites`, language, timeZone, expiresInDays ? { expiresInDays } : {}, null, cancelToken);
}
